import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';

export default function SignupPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', referralCode: '', over13: false, marketing: false });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [referralFromLink, setReferralFromLink] = useState('');

  // Auto-detect referral code from URL ?ref=XXXXX
  useEffect(() => {
    const hash = window.location.hash; // e.g. #/signup?ref=DAV1234
    const query = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(query);
    const ref = params.get('ref');
    if (ref) {
      setReferralFromLink(ref);
      setForm(f => ({ ...f, referralCode: ref }));
    }
  }, []);

  function update(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleSignup(e) {
    e.preventDefault();
    setError('');
    // We deliberately do NOT collect date of birth at signup (Apple Guideline
    // 5.1.1(v) — only collect what's essential to core functionality). The age
    // gate is the tick-box below. Members can optionally add their birthday
    // later in their profile to opt into the birthday-bonus feature.
    if (!form.over13) {
      setError('Please confirm you are 13 or over to create an account.');
      return;
    }
    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { first_name: form.firstName, last_name: form.lastName } },
    });

    if (signUpError) { setError(signUpError.message); setLoading(false); return; }

    // Save extra profile fields (marketing consent, referral code) if provided
    if (data?.user) {
      // Always record the marketing choice explicitly (opt-in, default off),
      // so consent reflects exactly what the member ticked.
      const profileUpdates = { marketing_opt_in: form.marketing };
      if (form.referralCode) profileUpdates.referred_by_code = form.referralCode.toUpperCase();
      await supabase.from('profiles').update(profileUpdates).eq('id', data.user.id);
    }

    setLoading(false);
    nav('/');
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#1A1A1A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 400, color: '#fff', letterSpacing: '0.04em' }}>team <span style={{ fontWeight: 800 }}>breakthrough</span></div>
        <div style={{ color: '#FF6200', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em', marginTop: 6 }}>BOX · BATH · CORSHAM</div>
      </div>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 380 }}>
        <div style={{
          background: 'linear-gradient(135deg, #FF6200 0%, #FF8533 100%)',
          color: '#fff',
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{ fontSize: '1.6rem', lineHeight: 1 }}>🎁</div>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sign up today</div>
            <div style={{ fontSize: '0.92rem', fontWeight: 600, marginTop: 2 }}>Get 20 bonus wellness points</div>
          </div>
        </div>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Create Account</h2>
        {referralFromLink && (
          <div style={{ fontSize: '0.82rem', color: 'var(--green)', fontWeight: 600, marginBottom: 16 }}>
            ✓ Referral code applied: {referralFromLink}
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSignup}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">First name</label>
              <input className="form-input" type="text" value={form.firstName} onChange={e => update('firstName', e.target.value)} required />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Last name</label>
              <input className="form-input" type="text" value={form.lastName} onChange={e => update('lastName', e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => update('email', e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={form.password} onChange={e => update('password', e.target.value)} required minLength={8} />
          </div>
          <div className="form-group">
            <label className="form-label">Referral code <span style={{ color: 'var(--grey-500)', fontWeight: 400 }}>(optional)</span></label>
            <input className="form-input" type="text" placeholder="e.g. DAV1234" value={form.referralCode}
              onChange={e => update('referralCode', e.target.value.toUpperCase())}
              style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.over13} onChange={e => update('over13', e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, accentColor: '#FF6200' }} />
            <span style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.4 }}>
              I confirm I am 13 years of age or over.
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.marketing} onChange={e => update('marketing', e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, accentColor: '#FF6200' }} />
            <span style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.4 }}>
              Email me occasional offers, events and wellness tips <span style={{ color: 'var(--grey-500)' }}>(optional — you can change this any time)</span>.
            </span>
          </label>
          <button className="btn btn-primary btn-full" type="submit" disabled={loading} style={{ marginTop: 16 }}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.72rem', color: '#999', lineHeight: 1.5 }}>
          By creating an account you agree to our{' '}
          <a href="https://www.breakthroughgym.co.uk/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#FF6200' }}>Privacy Policy</a>.
        </p>
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: '0.9rem', color: '#666' }}>
          Already a member? <a href="/#/login" style={{ color: '#FF6200', fontWeight: 600 }}>Log in</a>
        </p>
      </div>
    </div>
  );
}
